import React, { FC, ReactElement, useState } from 'react';

import classNames from 'classnames';
import { useIntl } from 'react-intl';

import { IExerciseNameCellProps } from './exercise-name-cell.interface';
import styles from './exercise-name-cell.module.scss';

import { AnchorElement } from 'types';
import { extractLinksFromText } from 'utils';
import { ExerciseCategoryId } from 'views/components/Exercise/exercise.interface';
import { LinkPreviewCard } from 'views/components/LinkPreviewCard';
import { Bookmark, Lightning, Headline4, Popover, Text } from 'views/components/UI';

const ExerciseNameCell: FC<IExerciseNameCellProps> = ({
  name,
  exercise,
}): ReactElement => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<AnchorElement>(null);

  if (!exercise) {
    return <>{name}</>;
  }

  const handleClose = (): void => setAnchorEl(null);

  const isPractice = exercise.categoryId === ExerciseCategoryId.PRACTICE;
  const CategoryIcon = isPractice ? Lightning : Bookmark;
  const logoClassNames = classNames(styles.logo, {
    [styles['logo--practice']]: isPractice,
  });

  const { segments } = extractLinksFromText(exercise.description);
  const hasContent = segments.some(
    segment => segment.type === 'link' || segment.content.trim(),
  );

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={e => setAnchorEl(current => (current ? null : e.currentTarget))}
      >
        {name}
      </button>
      {anchorEl && (
        <Popover
          anchorEl={anchorEl}
          handleClose={handleClose}
          role="dialog"
          aria-label={exercise.name}
        >
          <div className={styles.details}>
            <Headline4>{exercise.name}</Headline4>
            <div className={styles.category}>
              <figure className={logoClassNames}>
                <CategoryIcon size="12px" color="currentColor" />
              </figure>
              <Text variant="bodySmall" color="var(--sk-text-subtle)">
                {exercise.category}
              </Text>
            </div>
            {hasContent ? (
              segments.map((segment, index) =>
                segment.type === 'link' ? (
                  <LinkPreviewCard
                    key={`link-${segment.url}-${index}`}
                    url={segment.url}
                    compact
                  />
                ) : (
                  segment.content.trim() && (
                    <Text
                      key={`text-${index}`}
                      variant="bodySmall"
                      color="var(--sk-text-subtle)"
                    >
                      {segment.content}
                    </Text>
                  )
                ),
              )
            ) : (
              <Text variant="bodySmall" color="var(--sk-text-subtle)">
                {intl.formatMessage({ id: 'exercise-name-cell.no-description' })}
              </Text>
            )}
          </div>
        </Popover>
      )}
    </>
  );
};

export { ExerciseNameCell };
